<template>
    <authenticated-layout>
        <v-card class="pa-6">
            <v-card-title
                class="d-flex justify-space-between align-center pa-0"
            >
                <h2
                    class="font-semibold text-base text-gray-800 leading-tight"
                    @click="salesSpyMode = !salesSpyMode"
                >
                    {{ customer.name }} {{ customer.address }}

                    <v-btn
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="secondary"
                        @click="
                            syncCustomerNameAddressFromPipedrive(customer.id)
                        "
                    >
                        {{ $t("customers.syncPD") }}
                    </v-btn>
                </h2>

                <v-btn
                    variant="flat"
                    class="ml-2"
                    size="small"
                    color="primary"
                    href="/customers"
                >
                    {{ $t("customers.return") }}
                </v-btn>
            </v-card-title>

            <div v-if="heatPumpsEnabled" class="mt-10">
                <div class="d-flex align-center bg-gray-100 pa-2 rounded-md">
                    <h3 class="font-weight-bold">
                        {{ $t("customers.heatPumpDeals") }}
                    </h3>

                    <v-btn
                        v-if="customer.questionnaire_present"
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="primary"
                        :href="`/customers/${customer.id}/heat-pump-offer/create`"
                    >
                        {{ $t("customers.newOffer") }}
                    </v-btn>
                </div>

                <div class="d-flex justify-end mt-4">
                    <h3>{{ $t("customers.questionnaire") }}</h3>

                    <v-btn
                        v-if="!customer.questionnaire_present"
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="blue"
                        :href="`/customers/${customer.id}/questionnaire/create`"
                    >
                        {{ $t("customers.createQuestionnaire") }}
                    </v-btn>

                    <div v-else class="d-flex flex-col ml-2">
                        <v-btn
                            variant="flat"
                            size="small"
                            color="secondary"
                            :href="`/customers/${customer.id}/questionnaire/pdf`"
                            target="_blank"
                        >
                            {{ $t("customers.printQuestionnaire") }}
                        </v-btn>

                        <v-btn
                            variant="flat"
                            class="mt-1"
                            size="small"
                            color="blue"
                            :href="`/customers/${customer.id}/questionnaire/edit`"
                        >
                            {{ $t("customers.editQuestionnaire") }}
                        </v-btn>
                    </div>
                </div>

                <div class="overflow-x-auto mt-6">
                    <table>
                        <tr>
                            <th class="text-left">ID</th>
                            <th class="text-left">
                                {{ $t("customers.pump") }}
                            </th>
                            <th class="text-left">
                                {{ $t("water-container.container") }}
                            </th>
                            <th class="text-left">KW</th>
                            <th v-if="godmode" class="text-left">
                                {{ $t("customers.buyingPrice") }}
                            </th>
                            <th v-if="godmode" class="text-left">
                                {{ $t("customers.extrasCosts") }}
                            </th>
                            <th v-if="godmode" class="text-left">
                                {{ $t("customers.netPriceBase") }}
                            </th>
                            <th class="text-left">
                                {{ $t("customers.netPrice") }}
                            </th>
                            <th
                                class="text-left"
                                v-if="godmode || salesSpyMode"
                            >
                                Premium
                            </th>
                            <th
                                class="text-left"
                                v-if="godmode || salesSpyMode"
                            >
                                Discount
                            </th>
                            <th
                                class="text-left"
                                v-if="godmode || salesSpyMode"
                            >
                                {{ $t("customers.advisorsCommission") }}
                            </th>
                            <th class="text-left" v-if="godmode">
                                {{ $t("customers.companyProfit") }}
                            </th>
                            <th class="text-left">
                                {{ $t("customers.date") }}
                            </th>
                            <th class="text-left">
                                {{ $t("customers.expires") }}
                            </th>
                            <th class="text-left">
                                {{ $t("customers.author") }}
                            </th>
                            <th style="width: 160px">
                                {{ $t("customers.actions") }}
                            </th>
                            <th class="text-left">
                                {{ $t("customers.files") }}
                            </th>
                        </tr>

                        <tr
                            v-for="offer in customer.heat_pump_offers"
                            :class="{
                                'text-red-500':
                                    new Date(offer.expires_at) < now,
                            }"
                            :key="offer.id"
                        >
                            <td>{{ offer.id }}</td>
                            <td>
                                {{ offer.heatpump.brand }}
                                {{ offer.heatpump.model }}
                            </td>
                            <td>
                                {{ offer.water_container?.formattedName }}
                            </td>
                            <td>{{ offer.heatpump.kw }}</td>
                            <td v-if="godmode">
                                {{
                                    $numberHelper.formatMoney(
                                        offer.current_buying_price_net
                                    )
                                }}
                            </td>
                            <td v-if="godmode">
                                {{
                                    $numberHelper.formatMoney(offer.extrasCosts + (offer.water_container !== null ? offer.water_container.buying_price : 0))
                                }}
                            </td>
                            <td v-if="godmode">
                                {{
                                    $numberHelper.formatMoney(
                                        offer.base_selling_price_net
                                    )
                                }}
                            </td>
                            <td>
                                {{
                                    $numberHelper.formatMoney(
                                        offer.selling_price_net
                                    )
                                }}
                            </td>
                            <td v-if="godmode || salesSpyMode"> <!-- premium -->
                                {{
                                    $numberHelper.formatMoney(
                                        Math.round(offer.selling_price_net - offer.base_selling_price_net + parseFloat(offer.discount))
                                    )
                                }}
                            </td>
                            <td v-if="godmode || salesSpyMode">
                                {{
                                    $numberHelper.formatMoney(
                                        offer.discount
                                    )
                                }}
                            </td>
                            <td v-if="godmode || salesSpyMode">
                                {{
                                    $numberHelper.formatMoney(
                                        offer.sales_commission
                                    )
                                }}
                            </td>
                            <td v-if="godmode">
                                {{
                                    $numberHelper.formatMoney(
                                        offer.totalCompanyProfit
                                    )
                                }}
                            </td>
                            <td>{{ parseDate(offer.created_at) }}</td>
                            <td
                                :class="{
                                    'font-bold':
                                        new Date(offer.expires_at) < now,
                                }"
                            >
                                {{ parseDate(offer.expires_at) }}
                            </td>
                            <td>{{ offer.author.name }}</td>
                            <td class="text-right">
                                <div class="whitespace-nowrap">
                                    <v-btn
                                        variant="flat"
                                        size="x-small"
                                        color="primary"
                                        :href="`/customers/${customer.id}/heat-pump-offer/${offer.id}/view`"
                                    >
                                        {{ $t("customers.view") }}
                                    </v-btn>
                                    <div v-if="godmode">
                                        <v-btn
                                            v-if="!offer.won_offer"
                                            class="ml-1"
                                            variant="flat"
                                            size="x-small"
                                            color="green"
                                            @click="markDealAsWon(offer, 'heatpump')"
                                        >
                                            {{ $t("customers.Mark deal as won button") }}
                                        </v-btn>
                                        <v-btn
                                            v-else
                                            class="ml-1"
                                            variant="flat"
                                            size="x-small"
                                            color="red"
                                            @click="unmarkDealAsWon(offer, 'heatpump')"
                                        >
                                            {{ $t("customers.Unmark deal as won button") }}
                                        </v-btn>
                                    </div>
                                </div>

                                <div v-if="offer.pdf_offer_file" class="whitespace-nowrap">
                                    <v-btn
                                        v-if="
                                            offer.pdf_offer_file.file_status ===
                                            'ready'
                                        "
                                        variant="flat"
                                        size="x-small"
                                        class="mt-1"
                                        color="secondary"
                                        target="_blank"
                                        :href="
                                            route('download-heatpump-pdf', {
                                                customerId: customer.id,
                                                offerId: offer.id,
                                            })
                                        "
                                    >
                                        {{ $t("customers.pdf offer") }}
                                    </v-btn>

                                    <v-btn
                                        v-else-if="
                                            offer.pdf_offer_file.file_status ===
                                            'error'
                                        "
                                        variant="flat"
                                        size="x-small"
                                        class="mt-1"
                                        color="error"
                                        disabled
                                    >
                                        Error
                                    </v-btn>

                                    <v-btn
                                        v-else
                                        variant="flat"
                                        size="x-small"
                                        class="mt-1"
                                        color="secondary"
                                        @click="$inertia.reload()"
                                    >
                                        Processing...
                                    </v-btn>

                                    <v-btn
                                        variant="flat"
                                        size="x-small"
                                        class="mt-1 ml-1"
                                        color="red"
                                        @click="deleteHPPdfOfferFile(offer)"
                                    >X
                                    </v-btn>
                                </div>

                                <v-btn
                                    v-else
                                    variant="flat"
                                    size="x-small"
                                    class="mt-1"
                                    color="secondary"
                                    @click="generateHeatPumpPdfOffer(offer)"
                                >
                                    {{ $t("customers.pdf offer generate") }}
                                </v-btn>
                            </td>
                            <td>
                                <ul v-if="offer.heatpump.datasheets.length > 0">
                                    <li
                                        v-for="datasheet in offer.heatpump
                                            .datasheets"
                                    >
                                        <a
                                            :href="`/datasheet/${datasheet.id}`"
                                            :title="datasheet.name"
                                        >
                                            ⬇️
                                        </a>
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>

            <v-divider class="mt-6"/>

            <div
                class="d-flex justify-space-between align-center mt-4 bg-gray-100 pa-2 rounded-md"
            >
                <div class="d-flex align-center">
                    <h3 class="font-weight-bold">
                        {{ $t("customers.photovoltaicOffers") }}
                    </h3>

                    <v-btn
                        v-if="customer.annual_kwh_consumption > 0"
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="primary"
                        :href="`/offers/create/${customer.id}`"
                    >
                        {{ $t("customers.newOffer") }}
                    </v-btn>
                </div>

                <a
                    :href="comparisonLink"
                    target="_blank"
                    v-show="compareOffers.length > 1"
                    class="bg-amber btn float-right"
                >{{ $t("customers.compareTheOffers") }}
                </a>
            </div>

            <div class="d-flex align-center mt-6 w-96">
                <v-text-field
                    v-model="annualConsumption"
                    type="number"
                    :label="$t('customers.consumption')"
                    variant="outlined"
                    density="compact"
                    min="2000"
                    step="250"
                    hide-details
                />

                <v-btn
                    variant="flat"
                    class="ml-2"
                    size="small"
                    color="secondary"
                    @click="updateConsumption"
                >
                    Save
                </v-btn>
            </div>

            <div class="overflow-x-auto mt-6">
                <table>
                    <tr>
                        <th>ID</th>
                        <th>{{ $t("customers.modules") }}</th>
                        <th>{{ $t("customers.inverter") }}</th>
                        <th>{{ $t("customers.battery") }}</th>
                        <th>{{ $t("customers.numberOfOptics") }}</th>
                        <th>{{ $t("customers.installationSize") }}</th>
                        <th v-if="godmode">
                            {{ $t("customers.netPriceBase") }}
                        </th>
                        <th>
                            {{ $t("customers.netPrice") }}
                            <span v-if="godmode">
                                ({{ $t("customers.offered") }})
                            </span>
                        </th>
                        <th v-if="godmode">
                            {{ $t("customers.premiumAdvisers") }}
                        </th>
                        <th v-if="godmode">{{ $t("customers.discount") }}</th>
                        <th v-if="godmode || salesSpyMode">
                            {{ $t("customers.advisorsCommission") }}
                        </th>
                        <th>{{ $t("customers.date") }}</th>
                        <th>{{ $t("customers.expires") }}</th>
                        <th>{{ $t("customers.author") }}</th>
                        <th style="width: 160px">
                            {{ $t("customers.actions") }}
                        </th>
                        <th style="width: 90px">
                            {{ $t("customers.compare") }}
                        </th>
                        <th>{{ $t("customers.files") }}</th>
                    </tr>

                    <tr
                        v-for="offer in customer.solar_panel_offers"
                        :class="{
                            'text-red-500': new Date(offer.expires_at) < now,
                        }"
                        :key="`sp-offer-${offer.id}`"
                    >
                        <td>{{ offer.id }}</td>
                        <td>
                            {{ offer.panelsNumber }}x
                            {{ offer.solar_panel.brand }}
                            {{ offer.solar_panel.model }}
                        </td>
                        <td>
                            {{
                                offer.inverter_quantity > 1
                                    ? offer.inverter_quantity + " x "
                                    : ""
                            }}{{ offer.inverter.brand }}
                            {{ offer.inverter.model }}
                        </td>
                        <td>
                            {{
                                offer.battery
                                    ? offer.battery.brand +
                                    " " +
                                    offer.battery.model
                                    : "-"
                            }}
                        </td>
                        <td>{{ offer.optimizersNumber }}</td>
                        <td>
                            {{
                                offer.panelsNumber *
                                offer.solar_panel.power_output
                            }}
                            Wp
                        </td>
                        <td v-if="godmode">
                            {{
                                $numberHelper.formatMoney(
                                    offer.baseSellingPriceNet -
                                    offer.salesPremium
                                )
                            }}
                        </td>
                        <td>
                            {{
                                $numberHelper.formatMoney(offer.sellingPriceNet)
                            }}
                        </td>
                        <td v-if="godmode">
                            <span v-if="offer.salesPremium">{{
                                    $numberHelper.formatMoney(offer.salesPremium)
                                }}</span>
                        </td>
                        <td v-if="godmode">
                            {{ $numberHelper.formatMoney(offer.discount) }}
                        </td>
                        <td v-if="godmode || salesSpyMode">
                            {{
                                $numberHelper.formatMoney(offer.salesCommission)
                            }}
                        </td>
                        <td>{{ parseDate(offer.created_at) }}</td>
                        <td
                            :class="{
                                'font-bold': new Date(offer.expires_at) < now,
                            }"
                        >
                            {{ parseDate(offer.expires_at) }}
                        </td>
                        <td>{{ offer.author.name }}</td>
                        <td class="text-right">
                            <div class="whitespace-nowrap">
                                <v-btn
                                    v-if="offer.extras && offer.extras.public"
                                    variant="flat"
                                    size="x-small"
                                    color="primary"
                                    :href="`/offers/view/${offer.id}`"
                                >
                                    {{ $t("customers.view") }}
                                </v-btn>
                                <div v-if="godmode">
                                    <v-btn
                                        v-if="!offer.won_offer"
                                        class="ml-1"
                                        variant="flat"
                                        size="x-small"
                                        color="green"
                                        @click="markDealAsWon(offer, 'solar')"
                                    >
                                        {{ $t("customers.Mark deal as won button") }}
                                    </v-btn>
                                    <v-btn
                                        v-else
                                        class="ml-1"
                                        variant="flat"
                                        size="x-small"
                                        color="red"
                                        @click="unmarkDealAsWon(offer, 'solar')"
                                        v-if="godmode"
                                    >
                                        {{ $t("customers.Unmark deal as won button") }}
                                    </v-btn>
                                </div>
                            </div>

                            <div v-if="offer.pdf_offer_file" class="whitespace-nowrap">
                                <v-btn
                                    v-if="
                                        offer.pdf_offer_file.file_status ===
                                        'ready'
                                    "
                                    variant="flat"
                                    size="x-small"
                                    class="mt-1"
                                    color="secondary"
                                    target="_blank"
                                    :href="
                                        route('download-solar-pdf', {
                                            customerId: customer.id,
                                            offerId: offer.id,
                                        })
                                    "
                                >
                                    {{ $t("customers.pdf offer") }}
                                </v-btn>

                                <v-btn
                                    v-else-if="
                                        offer.pdf_offer_file.file_status ===
                                        'error'
                                    "
                                    variant="flat"
                                    size="x-small"
                                    class="mt-1"
                                    color="error"
                                    disabled
                                >
                                    Error
                                </v-btn>

                                <v-btn
                                    v-else
                                    variant="flat"
                                    size="x-small"
                                    class="mt-1"
                                    color="secondary"
                                    @click="$inertia.reload()"
                                >
                                    Processing...
                                </v-btn>

                                <v-btn
                                    variant="flat"
                                    size="x-small"
                                    class="mt-1 ml-1"
                                    color="red"
                                    @click="deleteSolarPdfOfferFile(offer)"
                                >X
                                </v-btn>
                            </div>

                            <v-btn
                                v-else
                                variant="flat"
                                size="x-small"
                                class="mt-1"
                                color="secondary"
                                @click="generateSolarPdfOffer(offer)"
                            >
                                {{ $t("customers.pdf offer generate") }}
                            </v-btn>
                        </td>
                        <td>
                            <v-btn
                                variant="flat"
                                size="x-small"
                                :class="{'bg-amber': compareOffers.includes(offer.id)}"
                                :disabled="
                                    !offersAvailableToCompare.includes(offer.id)
                                "
                                @click="toggleOfferToCompare(offer.id)"
                            >
                                {{ $t("customers.compare") }}
                            </v-btn>
                        </td>
                        <td>
                            <ul v-if="offer.solar_panel.datasheets.length > 0">
                                <li
                                    v-for="datasheet in offer.solar_panel
                                        .datasheets"
                                >
                                    <a
                                        :href="`/datasheet/${datasheet.id}`"
                                        :title="datasheet.name"
                                    >⬇️</a
                                    >
                                </li>
                            </ul>
                            <ul v-if="offer.inverter.datasheets.length > 0">
                                <li
                                    v-for="datasheet in offer.inverter
                                        .datasheets"
                                >
                                    <a
                                        :href="`/datasheet/${datasheet.id}`"
                                        :title="datasheet.name"
                                    >⬇️</a
                                    >
                                </li>
                            </ul>
                        </td>
                    </tr>
                </table>
            </div>
        </v-card>

        <v-dialog
            v-model="dealWonDialog"
            transition="dialog-bottom-transition"
            width="auto"
        >
            <v-card>
                <v-card-title>
                    <span v-if="dealWonForm.type" class="text-h5">{{ $t("customers.Mark heatpump deal as won") }}</span>
                    <span v-else class="text-h5">{{ $t("customers.Mark solar deal as won") }}</span>
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-row>
                            <v-col cols="7">
                                <v-list-subheader>
                                    {{ $t("customers.Pick a date when the deal won", {id: dealWonForm.offerId}) }}
                                </v-list-subheader>
                            </v-col>
                            <v-col cols="5">
                                <VueDatePicker
                                    :teleport="true"
                                    :enable-time-picker="false"
                                    text-input v-model="dealWonForm.dateWon"
                                    format="yyyy-MM-dd"
                                />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="7">
                                <v-list-subheader>
                                    {{ $t("customers.Offer num") }}
                                    ( {{ dealWonForm.type === 'solar' ? suggestedPCOfferNum : suggestedPVOfferNum }} )
                                </v-list-subheader>
                            </v-col>
                            <v-col cols="5">
                                <v-text-field
                                    v-model="dealWonForm.offerNum"
                                    variant="outlined"
                                    density="compact"
                                    type="input"
                                />
                            </v-col>
                        </v-row>
                        <div class="text-sm">
                            {{ $t("customers.Leave offer number empty to generate it automatically") }}
                        </div>
                    </v-container>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey-darken-1"
                        variant="flat"
                        @click="dealWonDialog = false"
                    >
                        {{ $t("customers.Cancel") }}
                    </v-btn>
                    <v-btn
                        color="blue-darken-1"
                        variant="flat"
                        :disabled="dealWonForm.dateWon === null"
                        @click="submitDealAsWon()"
                    >
                        {{ $t("customers.Mark") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <Toast v-if="toast" :key="toast" :toast="toast"/>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated";
import EditIcon from "@/Components/Icons/EditIcon";
import SelectInput from "@/Components/Shared/SelectInput";
import BreezeInput from "@/Components/Input";
import find from "lodash/find";
import DownArrowIcon from "@/Components/Icons/DownArrowIcon";
import QuestionnaireSidebar from "@/Components/Partials/QuestionnaireSidebar";
import {VueElement} from "vue";
import Toast from "@/Components/Toast";

export default {
    props: {
        customer: {
            type: Object,
            required: true,
        },
        toast: {
            type: Object,
            required: false
        },
        suggestedPVOfferNum: {
            type: String,
            default: () => ''
        },
        suggestedPCOfferNum: {
            type: String,
            default: () => ''
        }
    },
    components: {
        QuestionnaireSidebar,
        DownArrowIcon,
        EditIcon,
        AuthenticatedLayout,
        SelectInput,
        BreezeInput,
        Toast,
    },
    data() {
        return {
            now: new Date(),
            godmode: this.$page.props.auth.user.role !== "sales",
            salesSpyMode: false,
            compareOffers: [],
            heatPumpsEnabled: process.env.MIX_HEAT_PUMPS_ENABLED === "true",
            annualConsumption: this.customer.annual_kwh_consumption,
            dealWonDialog: false,
            dealWonForm: {
                offerNum: null,
                dateWon: null,
                type: null,
                offerId: null
            }
        };
    },
    mounted() {
        this.checkOffersPdfFileStatus();
    },
    computed: {
        offersAvailableToCompare() {
            let offers = this.customer.solar_panel_offers;
            if (this.compareOffers.length === 0) {
                return offers.map((o) => o["id"]);
            } else {
                let firstOffer = offers.find((o) => {
                        return o.id === this.compareOffers[0];
                    }),
                    offersOnSameAddress = offers.filter((o) => {
                        return o.clientAddress === firstOffer.clientAddress;
                    });
                return offersOnSameAddress.map((o) => o["id"]);
            }
        },
        comparisonLink() {
            return "/offers/compare/" + this.compareOffers.join(",");
        },
    },
    methods: {
        checkOffersPdfFileStatus() {
            const offerKeys = ["solar_panel_offers", "heat_pump_offers"];

            for (let x in offerKeys) {
                let offers = this.customer[offerKeys[x]];

                if (offers.length && this.processingFileExist(offers)) {
                    let interval = setInterval(async () => {
                        let fileIds = this.getProcessingPdfOfferFileIds(offers)
                        let routes = fileIds.map(fileId => this.route('pdf-offer-file-status', {fileId}))
                        let data = await Promise.all(routes.map(route => axios.get(route)))

                        for (let x in data) {
                            let obj = data[x].data

                            if (obj.status === 'ready') {
                                let index = offers.findIndex(offer => offer.pdf_offer_file_id === obj.id);
                                offers[index].pdf_offer_file.file_status = obj.status
                            }
                        }

                        let processingOffers = data.filter(offerData => offerData.data.status === 'new')

                        if (processingOffers.length === 0) {
                            clearInterval(interval)
                        }
                    }, 4000);
                }
            }
        },
        isPdfOfferFileStatusProcessing(offer) {
            return offer.pdf_offer_file && offer.pdf_offer_file.file_status === 'new';
        },
        processingFileExist(offers) {
            return offers.some(offer => this.isPdfOfferFileStatusProcessing(offer));
        },
        getProcessingPdfOfferFileIds(offers) {
            return offers.filter(offer => this.isPdfOfferFileStatusProcessing(offer)).map(offer => offer.pdf_offer_file.id);
        },
        isExpired(expires) {
            return new Date(expires) < new Date();
        },
        parseDate(date) {
            var mydate = new Date(date);
            return (
                mydate.toLocaleDateString() + " " + mydate.toLocaleTimeString()
            );
        },
        toggleOfferToCompare(id) {
            if (this.compareOffers.includes(id)) {
                var index = this.compareOffers.indexOf(id);
                if (index !== -1) {
                    this.compareOffers.splice(index, 1);
                }
            } else {
                if (this.compareOffers.length <= 3) {
                    this.compareOffers.push(id);
                } else {
                    alert(this.$t("customers.selectUpTo4Offers"));
                }
            }
        },
        updateConsumption() {
            this.$inertia.patch(
                `/customers/${this.customer.id}/update-consumption`,
                {
                    id: this.customer.id,
                    consumption: this.annualConsumption,
                }
            );
        },
        syncCustomerNameAddressFromPipedrive() {
            this.$inertia.post("/sync-customer-name-address-from-pipedrive", {
                customerId: this.customer.id,
            });
        },
        generateHeatPumpPdfOffer(offer) {
            const routeGenerate = this.route("generate-heatpump-pdf", {
                customerId: this.customer.id,
                offerId: offer.id,
            });

            const vm = this;

            axios.get(routeGenerate).then((response) => {
                console.log(response);
                if (response.data.success) {
                    vm.$inertia.reload();
                }
            });
        },
        generateSolarPdfOffer(offer) {
            const routeGenerate = this.route("generate-solar-pdf", {
                customerId: this.customer.id,
                offerId: offer.id,
            });

            const vm = this;

            axios.get(routeGenerate).then((response) => {
                console.log(response);
                if (response.data.success) {
                    vm.$inertia.reload();
                }
            });
        },
        deleteSolarPdfOfferFile(offer) {
            if (confirm("Delete solar offer pdf " + offer.id + ' ?')) {
                const routeDelete = this.route("delete-solar-pdf-offer", {
                    offerId: offer.id,
                });

                axios.post(routeDelete).then((response) => {
                    if (response.data.success) {
                        this.$inertia.reload();
                    }
                });
            }
        },
        deleteHPPdfOfferFile(offer) {
            if (confirm("Delete Heat pump offer pdf " + offer.id + ' ?')) {
                const routeDelete = this.route("delete-hp-pdf-offer", {
                    offerId: offer.id,
                });

                axios.post(routeDelete).then((response) => {
                    if (response.data.success) {
                        this.$inertia.reload();
                    }
                });
            }
        },
        markDealAsWon(offer, type) {
            window.scrollTo(0, 0);
            this.dealWonForm.offerId = offer.id
            this.dealWonForm.type = type
            this.dealWonDialog = true
        },
        unmarkDealAsWon(offer, type) {
            if (
                confirm(
                    this.$t(
                        "customers.Unmark deal as won question"
                    )
                )
            ) {
                this.$inertia.post(this.route('unmark-offer-as-won'), {
                    offerId: offer.id,
                    type: type
                });
            }
        },
        submitDealAsWon() {
            this.$inertia.post(this.route('submit-offer-as-won'), this.dealWonForm);
            this.dealWonForm.dateWon = null
            this.dealWonDialog = false
        },
    },
};
</script>

<style scoped>
table {
    width: 100%;
    font-size: 12px;
}

@media screen and (max-width: 1024px) {
    table {
        width: auto;
    }
}

th {
    padding: 10px 0px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    box-sizing: border-box;
    padding-left: 10px;
    border-bottom: 1px solid #aaa;
}

td {
    text-align: left;
    box-sizing: border-box;
    padding: 5px 0px;
    padding-left: 10px;
    border-bottom: 1px solid #ccc;
    vertical-align: top;
}
</style>
